import {
    ActionStatusStreamAction,
    ActionStatusStreamStatus,
    type AIGuideStreamHandlers,
    type StreamData,
} from "@khan/guide-types";
import {Errors, KAError} from "@khan/logging";

import {processStreamedResponse} from "./process-streamed-response.ts";

/**
 * Process a specific AI Guide streamed response data object.
 *
 * @param reader The reader to read the streamed response from.
 * @param handlers The handlers to call when a specific type of data is received.
 * @returns A promise of the complete buffered response.
 */
export const processStreamedAIResponse = async (
    // @ts-expect-error - TS2314 - Generic type 'ReadableStreamReader' requires 1 type argument(s).
    reader: ReadableStreamReader,
    {
        onAnswer,
        onAudio,
        onMetadata,
        onStopId,
        onCommand,
        onActionStatus,
    }: AIGuideStreamHandlers,
    onResponseStart?: null | (() => void),
): Promise<void> => {
    // This is used to build the full text received so far.
    let processedText = "";
    let prevTime = Date.now();

    await processStreamedResponse(
        reader,
        (jsonObject: StreamData): void => {
            switch (jsonObject.type) {
                case "stop-id":
                    try {
                        onStopId(jsonObject.data.interaction_id);
                    } catch (e: any) {
                        throw new KAError(
                            "Error handling stop-id stream data",
                            Errors.Internal,
                            {
                                cause: e,
                            },
                        );
                    }
                    break;

                case "command-stream":
                    try {
                        onCommand(
                            jsonObject.data.name,
                            jsonObject.data.content,
                        );
                    } catch (e: any) {
                        throw new KAError(
                            "Error handling command-stream stream data",
                            Errors.Internal,
                            {
                                cause: e,
                            },
                        );
                    }
                    break;

                case "text-stream":
                    try {
                        if (jsonObject.data.text) {
                            const currTime = Date.now();
                            const elapsed = currTime - prevTime;
                            prevTime = currTime;
                            processedText += jsonObject.data.text;
                            onAnswer(processedText, elapsed);
                        }
                    } catch (e: any) {
                        throw new KAError(
                            "Error handling text-stream stream data",
                            Errors.Internal,
                            {
                                cause: e,
                            },
                        );
                    }
                    break;

                case "text-stream-end":
                    try {
                        onActionStatus(
                            ActionStatusStreamAction.TEXT,
                            ActionStatusStreamStatus.SUCCESS,
                        );
                    } catch (e: any) {
                        throw new KAError(
                            "Error handling text-stream-end stream data",
                            Errors.Internal,
                            {
                                cause: e,
                            },
                        );
                    }
                    break;

                case "audio-stream":
                    try {
                        onAudio({
                            base64Audio: jsonObject.data.base64_audio ?? "",
                            transcript: jsonObject.data.transcript ?? "",
                        });
                    } catch (e: any) {
                        throw new KAError(
                            "Error handling audio-stream stream data",
                            Errors.Internal,
                            {
                                cause: e,
                            },
                        );
                    }
                    break;

                case "audio-stream-error":
                    try {
                        onAudio({
                            error: new KAError(
                                jsonObject.data.debugMessage,
                                Errors.Internal,
                            ),
                        });
                    } catch (e: any) {
                        throw new KAError(
                            "Error handling audio-stream-error stream data",
                            Errors.Internal,
                            {
                                cause: e,
                            },
                        );
                    }
                    break;
                case "metadata":
                    try {
                        onMetadata({
                            threadId: jsonObject.data.thread_id,
                            interactionId: jsonObject.data.interaction_id,
                            conversation: jsonObject.data.conversation,
                            conversationEntries:
                                jsonObject.data.conversation_entries,
                            remainingQuota:
                                jsonObject.data.quota_remaining_percent,
                            feedbackMap: jsonObject.data.feedback_map
                                ? new Map(
                                      Object.entries(
                                          jsonObject.data.feedback_map,
                                      ),
                                  )
                                : undefined,
                            restoredPromptVariables:
                                jsonObject.data.restored_prompt_variables,
                            entryComponentOutput:
                                jsonObject.data.entry_component_output,
                        });
                    } catch (e: any) {
                        throw new KAError(
                            "Error handling metadata stream data",
                            Errors.Internal,
                            {
                                cause: e,
                            },
                        );
                    }
                    break;

                case "action-status-stream":
                    try {
                        const {action, status} = jsonObject.data;
                        onActionStatus(action, status);
                    } catch (e: any) {
                        throw new KAError(
                            "Error handling action stream data",
                            Errors.Internal,
                            {
                                cause: e,
                            },
                        );
                    }
                    break;

                default:
                    // We ignore data we don't recognize.
                    // This means that the streamed response could be updated to
                    // add more response types and existing frontend clients will
                    // not fail, but instead, ignore that data.
                    break;
            }
        },
        onResponseStart,
    );
};
